import React, { useEffect, useState } from 'react';
import { ProjectRow } from '../../lib/types';
import { getProjects } from '../api/client';
import AuthenticatedLayout from '../components/layout/authenticated-layout';
import { useAuth } from '../hooks/useAuth';
import { DiscoveryBoard } from './discovery-board';
import { GovernanceBoard } from './governance-board';
import { PortfolioBoard } from './portfolio-board';
import IdeaDetail from './idea-detail';
import styles from './pipeline.module.css';

export default function PipelinePage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'discovery' | 'governance' | 'portfolio'>('discovery');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await getProjects();
      setProjects(response.data || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const projectsInDiscovery = projects.filter(
    p => ['NewIdea', 'GatheringFeedback', 'ReadyForGovernance'].includes(p.Status)
  );

  const projectsInGovernance = projects.filter(
    p => ['ReadyForGovernance', 'BelowTheLine', 'AboveTheLine'].includes(p.Status)
  );

  const projectsInPortfolio = projects.filter(
    p => ['InValidation', 'InPlanning', 'ReadyForDelivery', 'InDelivery', 'Complete'].includes(p.Status)
  );

  const handleProjectUpdate = () => {
    fetchProjects();
  };

  if (!user) {
    return <div className="error">Not authenticated</div>;
  }

  return (
    <AuthenticatedLayout
      user={user}
      title="Portfolio Pipeline"
      subtitle="View all ideas and projects across all phases"
      breadcrumbs={[{ label: 'Portfolio' }, { label: 'Pipeline' }]}
      onLogout={() => window.location.hash = '#/login'}
    >
      <div className={styles.pipelineContainer}>
        {/* Tab Navigation */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'discovery' ? styles.active : ''}`}
            onClick={() => {
              setActiveTab('discovery');
              setSelectedProjectId(null);
            }}
          >
            <span className={styles.tabIcon}>💡</span>
            Discovery ({projectsInDiscovery.length})
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'governance' ? styles.active : ''}`}
            onClick={() => {
              setActiveTab('governance');
              setSelectedProjectId(null);
            }}
          >
            <span className={styles.tabIcon}>⚖️</span>
            Governance ({projectsInGovernance.length})
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'portfolio' ? styles.active : ''}`}
            onClick={() => {
              setActiveTab('portfolio');
              setSelectedProjectId(null);
            }}
          >
            <span className={styles.tabIcon}>📦</span>
            Portfolio ({projectsInPortfolio.length})
          </button>
        </div>

        {/* Content Area */}
        <div className={styles.content}>
          {loading ? (
            <div className={styles.loading}>Loading pipeline...</div>
          ) : selectedProjectId ? (
            <IdeaDetail
              projectId={selectedProjectId}
              onClose={() => setSelectedProjectId(null)}
              onUpdate={handleProjectUpdate}
            />
          ) : (
            <>
              {activeTab === 'discovery' && (
                <DiscoveryBoard
                  projects={projectsInDiscovery}
                  onSelectProject={setSelectedProjectId}
                />
              )}
              {activeTab === 'governance' && (
                <GovernanceBoard
                  projects={projectsInGovernance}
                  onSelectProject={setSelectedProjectId}
                />
              )}
              {activeTab === 'portfolio' && (
                <PortfolioBoard
                  projects={projectsInPortfolio}
                  onSelectProject={setSelectedProjectId}
                />
              )}
            </>
          )}
        </div>
      </div>
    </AuthenticatedLayout>
  );
}
